import {
  Checkbox,
  FormControlLabel,
  makeStyles,
  TextField,
  Typography
} from "@material-ui/core";
import { Button } from "@coolremote/shared";
import clsx from "clsx";
import React from "react";
import { t } from "ttag";
import { registerNewDeviceStyle } from "../registerNewDevice.style";

export interface DeviceCredentialsValues {
  serial: string | null;
  pin: string | null;
  description?: string;
}

export interface DeviceCredentialsErrors {
  serial?: string;
  pin?: string;
  description?: string;
}

interface DeviceCredentialsFormProps {
  /** Current field values, owned by the parent's Formik instance */
  values: DeviceCredentialsValues;
  errors: DeviceCredentialsErrors;
  onSerialChange: (value: string) => void;
  onPinChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  /** QR scanner state, owned by the parent so it can drive the scanner itself */
  isCameraOpen: boolean;
  onToggleCamera: () => void;
  /** DOM id the html5-qrcode scanner renders into */
  qrCodeRegionId: string;
  qrImageSrc: string;
  confirmChecked: boolean;
  onConfirmToggle: () => void;
  isSubmitDisabled: boolean;
  /**
   * Rendered between the location field and the confirmation checkbox.
   * The authenticated screen passes its site picker here; the public
   * registration flow passes nothing.
   */
  extraFields?: React.ReactNode;
}

/**
 * The serial / PIN / QR / location / confirmation part of device registration.
 *
 * Presentational only: it holds no store state, no routing and no validation of
 * its own, so it can be rendered both by the authenticated add-device screen and
 * by the public pre-login registration flow (CM-168). It must be rendered inside
 * a Formik form — the parent owns the form state and the submit handler.
 *
 * Still built with Material UI on purpose, to keep the extraction from
 * RegisterNewDevice behaviour-preserving. Converting it to @coolremote/shared is
 * tracked separately in CM-642.
 */
const DeviceCredentialsForm: React.FC<DeviceCredentialsFormProps> = ({
  values,
  errors,
  onSerialChange,
  onPinChange,
  onDescriptionChange,
  isCameraOpen,
  onToggleCamera,
  qrCodeRegionId,
  qrImageSrc,
  confirmChecked,
  onConfirmToggle,
  isSubmitDisabled,
  extraFields
}) => {
  const useStyles = makeStyles(registerNewDeviceStyle);
  const classes = useStyles();

  const handleSerialChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSerialChange(event.target.value);
  };

  const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPinChange(event.target.value);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onDescriptionChange(event.target.value);
  };

  return (
    <>
      <div className={classes.addDeviceContainer}>
        <Typography className={classes.textStyle}>
          {t`Add Device`}
        </Typography>
        <TextField
          name="serial"
          value={values.serial}
          variant={"outlined"}
          placeholder={t`Device SN MAC`}
          className={classes.wrapperStyle}
          onChange={handleSerialChange}
          error={!!errors.serial && values.serial !== null}
          helperText={errors.serial && values.serial !== null && errors.serial}
        />
        <TextField
          name="pin"
          value={values.pin}
          variant={"outlined"}
          placeholder={t`Device PIN`}
          className={classes.wrapperStyle}
          onChange={handlePinChange}
          error={!!errors.pin && values.pin !== null}
          helperText={errors.pin && values.pin !== null && errors.pin}
        />
        {isCameraOpen && (
          <div id={qrCodeRegionId} />
        )}
        {!isCameraOpen &&
          <Typography className={classes.qrTypoStyle} style={{ marginBottom: 10 }}>
            {t`OR`}
          </Typography>
        }
        <div className={classes.QRWrapper}>
          <div className={classes.QRForm} onClick={onToggleCamera}>
            <div className={classes.qrDetailsContainer}>
              <img className={classes.QRImgStyle} src={qrImageSrc} alt={"QR code"} />
              <Typography className={classes.qrTypoStyle}>
                {t`Scan QR code`}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <TextField
        name="description"
        value={values.description || ""}
        variant={"outlined"}
        placeholder={t`Device Location`}
        className={classes.wrapperStyle}
        onChange={handleDescriptionChange}
        error={!!errors.description}
        helperText={errors.description}
      />
      {extraFields}
      <FormControlLabel
        control={
          <Checkbox
            color={"default"}
            checked={confirmChecked}
            onChange={onConfirmToggle}
          />
        }
        style={{
          marginBottom: 10,
          marginTop: 10,
          marginRight: 0
        }}
        classes={{
          label: classes.lableStyle,
          root: confirmChecked ? "" : classes.checkBoxRed
        }}
        label={t`I confirm I have the official approval to connect to the HVAC system using this device and start pulling all technical and control data using this solution.`}
      />
      <Button
        type="submit"
        variant="contained"
        className={clsx(classes.nextButton)}
        disabled={isSubmitDisabled}
      >
        {t`Next`}
      </Button>
    </>
  );
};

export default DeviceCredentialsForm;
